/** @jsx jsx */
import { gql } from '@apollo/client';
import { t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import type { DialogProps } from '@reach/dialog';
import { graphql } from 'gatsby';
import { jsx } from 'theme-ui';
import type { ContentLocale } from '../../useLocale';
import Dialog from '../Dialog';
import type { UserProfileCardAvatarData } from '../UserProfile/UserProfileCard.Avatar';
import RequestMeetingLogin from './RequestMeetingButton.Login';
import type { RequestMeetingProfileFormData } from './RequestMeetingButton.ProfileForm';
import RequestMeetingButtonProfileForm from './RequestMeetingButton.ProfileForm';
import RequestMeetingSubmissionError from './RequestMeetingButton.SubmissionError';
import SubmissionSuccess from './RequestMeetingButton.SubmissionSuccess';
import type { RequestMeetingButtonTimeFormData } from './RequestMeetingButton.TimeForm';
import RequestMeetingButtonTimeForm from './RequestMeetingButton.TimeForm';
import useMeetingRequestState from './RequestMeetingButton.useState';

type RequestMeetingDialogProps = Omit<DialogProps, 'children'> & {
  profile: RequestMeetingDialogData;
  collectionId: string;
  locale: ContentLocale;
};

/** The dialog containing the user flow for a meeting request. */
export default function RequestMeetingDialog({
  collectionId,
  locale,
  profile,
  onDismiss,
  ...rest
}: RequestMeetingDialogProps) {
  const { i18n } = useLingui();
  const { state, onContinue, onBack, meeting } = useMeetingRequestState({
    recipientId: profile.uid,
  });
  const [requestState, result] = state;
  switch (requestState) {
    case 'LOADING':
      return (
        <Dialog
          aria-label={i18n._(`Loading your profile…`)}
          onDismiss={onDismiss}
          sx={{ '&&': { maxWidth: 480 } }}
          {...rest}
        >
          <RequestMeetingLogin withProfile={profile} onDismiss={onDismiss} />
        </Dialog>
      );
    case 'LOGIN':
      return (
        <Dialog
          aria-label={i18n._(t`Log in to book a meeting`)}
          onDismiss={onDismiss}
          sx={{ '&&': { maxWidth: 480 } }}
          {...rest}
        >
          <RequestMeetingLogin withProfile={profile} onDismiss={onDismiss} />
        </Dialog>
      );
    case 'SELECT_DATE':
      return (
        <Dialog
          aria-label={i18n._(t`Propose a timeslot for your meeting`)}
          onDismiss={onDismiss}
          {...rest}
        >
          <RequestMeetingButtonTimeForm
            recipient={profile}
            onDismiss={onDismiss}
            initialValues={meeting}
            onContinue={onContinue}
          />
        </Dialog>
      );
    case 'REVIEW_PROFILE':
      return (
        <Dialog
          aria-label={'Review your profile'}
          onDismiss={onDismiss}
          {...rest}
        >
          <RequestMeetingButtonProfileForm
            recipient={profile}
            onDismiss={onDismiss}
            initialValues={meeting}
            onContinue={onContinue}
            onBack={onBack}
          />
        </Dialog>
      );
    case 'SUBMITTING':
      return (
        <Dialog
          aria-label={'Submitting meeting request…'}
          onDismiss={onDismiss}
          {...rest}
        >
          <RequestMeetingButtonProfileForm
            recipient={profile}
            onDismiss={onDismiss}
            initialValues={meeting}
            onContinue={onContinue}
            onBack={onBack}
          />
        </Dialog>
      );
    case 'SUCCESS':
      return (
        <Dialog
          aria-label={i18n._(t`Meeting request sent`)}
          onDismiss={onDismiss}
          {...rest}
        >
          <SubmissionSuccess recipient={profile} onDismiss={onDismiss} />
        </Dialog>
      );
    case 'ERROR':
      return (
        <Dialog
          aria-label={i18n._(t`Error booking a meeting`)}
          onDismiss={onDismiss}
          {...rest}
        >
          <RequestMeetingSubmissionError
            message={result}
            profile={profile}
            onDismiss={onDismiss}
          />
        </Dialog>
      );
    default:
      exhaustiveCheck(requestState);
  }
}

function exhaustiveCheck(_: never): never {
  throw new Error('Unhandled state');
}

RequestMeetingDialog.fragment = gql`
  fragment RequestMeetingDialog on SiteUserProfile {
    id
    displayName
    jobTitle
    companyName
    ...RequestMeetingButtonTimeForm
    ...RequestMeetingButtonProfileForm
  }
  ${RequestMeetingButtonTimeForm.fragment}
  ${RequestMeetingButtonProfileForm.fragment}
`;

export const fragment = graphql`
  fragment RequestMeetingDialog on Bottlebooks_SiteUserProfile {
    uid
    displayName
    jobTitle
    companyName
    ...RequestMeetingButtonTimeForm
    ...RequestMeetingButtonProfileForm
  }
`;

export type RequestMeetingDialogData = UserProfileCardAvatarData &
  RequestMeetingButtonTimeFormData &
  RequestMeetingProfileFormData & {
    uid: string;
    displayName: string | null;
    companyName?: string | null | undefined;
    jobTitle?: string | null | undefined;
  };
